// The TIME AXIS of one authored cell (HI-1, next/interval-training.md §9.1).
//
// Its own file, and not a corner of programEditBuild, for two reasons: the
// build file was fourteen lines from the 500 hard cap and this is the part
// that will keep growing (the group cadence authoring lands next), and every
// hop that touches work/rest goes through exactly these three functions, so
// they are easier to hold together than to find scattered.
//
// Pure. Keep free of React, UI and platform imports.

import { validateNumericField } from "./numericFieldValidation.js";

// Seconds cell (HI-1). Blank returns null, which the caller reads as "not
// prescribed" — for rest that means the existing ladder decides
// (exercise.defaultRestSec → profile.defaultRestSec), as every set does
// today.
//
// ⭐ The RULE is not restated here: it is `validateNumericField("seconds")`,
// the same one the field the coach types into already enforces. A second
// regex saying "whole seconds up to 3600" would be a copy free to drift
// from the message on screen, which is the worst kind — the two would
// disagree about what is valid and only one of them would be visible.
const parseSecondsToken = (v: string): number | null => {
  const raw = (v ?? "").trim();
  if (raw === "" || validateNumericField("seconds", raw) !== null) return null;
  return parseInt(raw, 10);
};

// Does this cell's time axis refuse to be saved? (HI-1)
//
// ⛔ This exists because "not prescribed" and "prescribed wrongly" were the
// same answer, and the build read both as absence. Typing 5000 into Work
// showed a red field and then saved a bout-less set anyway; picking "Time"
// and leaving the seconds empty did the same without even a red field. Both
// silently turn a countdown into an unbounded reps set.
// ⚠️ This comment used to claim the AMRAP floor was refused "one layer up".
// It was not: it was stripped in silence, and the promise stood here for two
// months describing a refusal nobody had written. `amrapWithTimeError` below
// is that refusal, added when the review caught the overclaim (HI-5).
export const timeAxisError = (
  workKind: "countdown" | "countup" | undefined,
  workDurationSec: string | undefined,
  restSec: string | undefined,
): "invalidSeconds" | null => {
  const rest = (restSec ?? "").trim();
  if (rest !== "" && validateNumericField("seconds", rest) !== null)
    return "invalidSeconds";
  if (workKind !== "countdown") return null;
  const secs = parseSecondsToken(workDurationSec ?? "");
  // A countdown with no length is not a bout, it is an unanswered question.
  return secs !== null && secs >= 1 ? null : "invalidSeconds";
};

// The time axis of one authored cell → the shape SetPrescription and
// PlannedSet both carry (HI-1). ONE place, because the program build and
// the plan build were byte-identical here and two copies of a rule this
// quiet is how the two sides come to disagree about what "30s" means.
//
// `amrapAllowed` is the rule, not a caller's opinion: a countdown bout is
// bounded by the clock, so an AMRAP floor on it is a contradiction, and
// reps 0 + intensity "none" is already the freestyle sentinel.
// ⚠️ It STRIPS. The author-facing refusal is `amrapWithTimeError` below, which
// stops the save before this ever runs; this stays as the last line of defence
// for a payload arriving from anywhere else (MCP, import, a future caller).
export const buildWorkForCell = (
  workKind: "countdown" | "countup" | undefined,
  workDurationSec: string | undefined,
  restSec: string | undefined,
): {
  work?: { kind: "countdown"; durationSec: number } | { kind: "countup" };
  restSec?: number;
  amrapAllowed: boolean;
} => {
  const secs =
    workKind === "countdown" ? parseSecondsToken(workDurationSec ?? "") : null;
  const work =
    workKind === "countdown" && secs !== null && secs >= 1
      ? ({ kind: "countdown", durationSec: secs } as const)
      : workKind === "countup"
        ? ({ kind: "countup" } as const)
        : undefined;
  const rest = parseSecondsToken(restSec ?? "");
  return {
    ...(work ? { work } : {}),
    ...(rest !== null ? { restSec: rest } : {}),
    amrapAllowed: work?.kind !== "countdown",
  };
};

// An AMRAP floor on a clock-bounded bout, refused instead of stripped (HI-5).
//
// The rule is HIS (`interval-training.md` §10 punto 2): *«per un atleta
// potrebbe essere 2 secondi e per un altro 45, non possiamo saperlo a priori»*
// — "as many as you can" and "for exactly 30 seconds" cannot both bound one
// set. ⛔ What his rule forbids is the FLOOR, never the ordinary rep count: a
// bout prescribed `10` reps is fine and is exactly what HI-5 restored.
//
// Safe to harden without touching stored data: no saved SetPrescription can
// carry `amrap` next to a countdown, because every build path has stripped the
// flag since HI-1 (buildWorkForCell's amrapAllowed). This only stops a coach
// who types `10+` into a timed row from saving it and never learning that half
// of what they wrote was thrown away.
export const amrapWithTimeError = (
  workKind: "countdown" | "countup" | undefined,
  reps: string | undefined,
): "amrapWithTime" | null =>
  workKind === "countdown" && /\+\s*$/.test((reps ?? "").trim())
    ? "amrapWithTime"
    : null;

